/**
 * Filter vocabularies for the card and collection LIST pages — the field
 * descriptors the shared FilterBuilder/FilterChips consume, in the same shape
 * `buildFilterFields` (kqlBuilder.js) produces for items:
 *
 *   { key, path, forceElement: false, label, dataType, options, system,
 *     partial: false, cardLabels: [] }
 *
 * The paths mirror the API's card and collection KQL subjects
 * (docs/plans/list-queries.md, "The vocabularies"). Ref fields (parent, card,
 * collection) and fixed enumerations (scope, status, role, element.type) are
 * `choice` fields with `options.choices`, so the builder offers a picker and
 * the chips show labels rather than slugs. Pure: no React, no antd, no HTTP.
 */
import { buildCardHierarchyList } from '../components/cards/CardVisuals';
import { dataTypes } from './dictionary';
import { normalizeId } from './ids';
import { pathKey } from './kqlBuilder';
import { COLLECTION_ROLE_OPTIONS } from './permissions';

/** A card's scope (`scope` on the cards subject). */
export const CARD_SCOPE_CHOICES = [
	{ value: 'global', label: 'Global' },
	{ value: 'collection', label: 'Collection card' },
	{ value: 'user', label: 'Unassigned' },
];

/** A collection's lifecycle (`status` on the collections subject). */
export const COLLECTION_STATUS_CHOICES = [
	{ value: 'active', label: 'Active' },
	{ value: 'archived', label: 'Archived' },
];

// What kind of collection (keepr-api docs/SCHEMA.md, the collections subject's
// `kind`): the account's personal collection, a global collection, or any
// other. Never empty.
export const COLLECTION_KIND_CHOICES = [
	{ value: 'personal', label: 'Personal' },
	{ value: 'global', label: 'Global' },
	{ value: 'standard', label: 'Standard' },
];

const roleLabelOf = role => (COLLECTION_ROLE_OPTIONS.find(option => option.value === role) || {}).label;

/** The caller's role on a collection (`role`) — the platform's one role vocabulary. */
export const COLLECTION_ROLE_CHOICES = [
	{ value: 'owner', label: 'Owner' },
	{ value: 'manage', label: roleLabelOf('manage') || 'Manager' },
	{ value: 'write', label: roleLabelOf('write') || 'Can edit' },
	{ value: 'read', label: roleLabelOf('read') || 'Can view' },
];

/** Every element dataType the platform knows, as picker choices. */
export const ELEMENT_TYPE_CHOICES = dataTypes.map(({ name, label }) => ({ value: name, label }));

/**
 * Element sets as picker choices `{ value: key | id, label: name }` (`set` on
 * the cards subject: the sets a card's effective chain carries). The key is
 * what the API resolves; a set without one is named by its id.
 */
export function elementSetRefChoices(elementSets = []) {
	const seen = new Set();
	const choices = [];
	(elementSets || []).forEach(set => {
		const value = set?.key || normalizeId(set?._id);
		if (!value || seen.has(value)) return;
		seen.add(value);
		choices.push({ value, label: set?.name || value });
	});
	return choices;
}

const field = (path, label, dataType, { choices, system = false } = {}) => ({
	key: pathKey(path),
	path: [...path],
	forceElement: false,
	label,
	dataType,
	options: choices ? { choices } : {},
	system,
	partial: false,
	cardLabels: [],
});

const SYSTEM_TIMESTAMPS = [
	['created', 'Created'],
	['updated', 'Updated'],
];

const timestampFields = () => SYSTEM_TIMESTAMPS.map(([name, label]) => field([name], label, 'date-time', { system: true }));

/**
 * Card refs as picker choices, in hierarchy order (parents before children,
 * siblings by name). The value is what the API resolves: the card's key when
 * it has one, else its id. Keys are unique per namespace, not globally — a
 * global `person` and a collection `person` both match `parent = person` —
 * so a repeated key collapses to one option.
 */
export function cardRefChoices(cards = []) {
	const seen = new Set();
	const choices = [];
	buildCardHierarchyList(cards || []).forEach(({ card }) => {
		const value = card?.key || normalizeId(card?._id);
		if (!value || seen.has(value)) return;
		seen.add(value);
		choices.push({ value, label: card?.name || value });
	});
	return choices;
}

/** Collections as picker choices `{ value: id, label: name }`, by name. */
export function collectionRefChoices(collections = []) {
	return (collections || [])
		.map(collection => {
			const value = normalizeId(collection?._id);
			if (!value) return null;
			return { value, label: collection?.name || value };
		})
		.filter(Boolean)
		.sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * The cards-list vocabulary. `cards` = the readable card definitions (the
 * app-wide cache); `collections` = the collections the caller can see.
 */
export function buildCardFilterFields({ cards = [], collections = [], elementSets = [] } = {}) {
	return [
		field(['name'], 'Name', 'text-small'),
		field(['key'], 'Key', 'text-small'),
		field(['description'], 'Description', 'text-small'),
		field(['text'], 'Text', 'text-small'),
		field(['scope'], 'Scope', 'choice', { choices: CARD_SCOPE_CHOICES }),
		field(['parent'], 'Parent card', 'choice', { choices: cardRefChoices(cards) }),
		field(['set'], 'Element set', 'choice', { choices: elementSetRefChoices(elementSets) }),
		field(['collection'], 'Used in collection', 'choice', { choices: collectionRefChoices(collections) }),
		field(['collections'], 'Collections using it', 'number'),
		field(['children'], 'Child cards', 'number'),
		field(['element'], 'Element', 'text-small'),
		field(['element', 'name'], 'Element name', 'text-small'),
		field(['element', 'label'], 'Element label', 'text-small'),
		field(['element', 'type'], 'Element type', 'choice', { choices: ELEMENT_TYPE_CHOICES }),
		...timestampFields(),
	];
}

/**
 * The collections-list vocabulary. `cards` = the readable card definitions,
 * for the `card` (member card) picker.
 */
/**
 * The parents the loaded collections name, as picker choices — the
 * collections subject's `parent` takes an id or a name, and a parent the
 * viewer cannot open still has its summary on each of its sub-collections'
 * rows (keepr-api § Sub-collections).
 */
export function parentChoices(collections = []) {
	const byId = new Map();
	(collections || []).forEach(collection => {
		const parent = collection?.parent;
		const id = normalizeId(parent?._id);
		if (id && !byId.has(id)) byId.set(id, { value: id, label: parent?.name || id });
	});
	return [...byId.values()].sort((a, b) => a.label.localeCompare(b.label));
}

export function buildCollectionFilterFields({ cards = [], collections = [] } = {}) {
	return [
		field(['name'], 'Name', 'text-small'),
		field(['description'], 'Description', 'text-small'),
		field(['text'], 'Text', 'text-small'),
		field(['card'], 'Card', 'choice', { choices: cardRefChoices(cards) }),
		field(['cards'], 'Cards', 'number'),
		field(['status'], 'Status', 'choice', { choices: COLLECTION_STATUS_CHOICES }),
		field(['role'], 'My role', 'choice', { choices: COLLECTION_ROLE_CHOICES }),
		field(['kind'], 'Kind', 'choice', { choices: COLLECTION_KIND_CHOICES }),
		field(['parent'], 'Parent collection', 'choice', { choices: parentChoices(collections) }),
		...timestampFields(),
	];
}

// The packages subject (keepr-api docs/SCHEMA.md "Subject: packages"):
// Discover's filter vocabulary. `categories` are the ones the catalog has.
export const PACKAGE_KIND_CHOICES = [
	{ value: 'collection', label: 'Collection' },
	{ value: 'bundle', label: 'Card bundle' },
	{ value: 'card', label: 'Card' },
];

export function buildPackageFilterFields({ categories = [] } = {}) {
	return [
		field(['name'], 'Name', 'text-small'),
		field(['text'], 'Text', 'text-small'),
		field(['kind'], 'Kind', 'choice', { choices: PACKAGE_KIND_CHOICES }),
		field(['category'], 'Category', 'choice', { choices: [...new Set(categories.filter(Boolean))].sort().map(c => ({ value: c, label: c })) }),
		field(['tags'], 'Tag', 'text-small'),
	];
}
